import React, { useState, useEffect } from 'react';
import StartView from './StartView';
import Sidebar from './Sidebar';
import DetailPage from './DetailPage';
import ThemeSelector from './ThemeSelector';
import AddDepartmentForm from './AddDepartmentForm';
import ProcessGenerator from './ProcessGenerator';
import MermaidEditorPage from './MermaidEditorPage';
import PapersRefiner from './papers-refiner/PapersRefiner';
import Studio from './studio/Studio';
import './Dashboard.css';

export interface Department {
  id: string;
  name: string;
  icon?: string;
  image: string;
  page: {
    title: string;
    benefit: string;
    features: string[];
  };
}

type View = 'papers-refiner' | 'start' | 'detail' | 'process-generator' | 'mermaid-editor' | 'studio';

const defaultDepartments: Department[] = [
  {
    id: 'it',
    name: 'IT',
    icon: '💻',
    image: 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=800&h=400&fit=crop',
    page: {
      title: 'Benutzer-Provisionierung',
      benefit: 'Beschleunigt Onboarding und reduziert Fehler.',
      features: [
        'Self-Service Antrag',
        'Rollenbasiertes Rechte-Set',
        'Audit-Log'
      ]
    }
  },
  {
    id: 'finance',
    name: 'Finanzen',
    icon: '💰',
    image: 'https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=800&h=400&fit=crop',
    page: {
      title: 'Budget-Übersicht',
      benefit: 'Transparente Planung und Kontrolle der Kostenstellen.',
      features: [
        'Soll/Ist Vergleich',
        'Quartals-Forecast',
        'CSV-Export'
      ]
    }
  },
  {
    id: 'hr',
    name: 'Personal',
    icon: '👥',
    image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&h=400&fit=crop',
    page: {
      title: 'Urlaubsübersicht',
      benefit: 'Schneller Überblick über Abwesenheiten.',
      features: [
        'Teamkalender',
        'Genehmigungsstatus',
        'Download Monatsliste'
      ]
    }
  }
];

const Dashboard: React.FC = () => {
  const [departments, setDepartments] = useState<Department[]>(() => {
    const saved = localStorage.getItem('departments');
    return saved ? JSON.parse(saved) : defaultDepartments;
  });

  const [selectedDepartment, setSelectedDepartment] = useState<Department | null>(null);
  const [currentView, setCurrentView] = useState<View>('papers-refiner');
  const [showAddForm, setShowAddForm] = useState(false);
  const [theme, setTheme] = useState<'light' | 'dark' | 'nord'>(() => {
    const saved = localStorage.getItem('theme');
    return (saved as 'light' | 'dark' | 'nord') || 'dark';
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  useEffect(() => {
    localStorage.setItem('departments', JSON.stringify(departments));
  }, [departments]);

  const handleDepartmentSelect = (department: Department) => {
    setSelectedDepartment(department);
    setCurrentView('detail');
  };

  const handleBack = () => {
    setSelectedDepartment(null);
    setCurrentView('start');
  };

  const handleShowProcessGenerator = () => {
    setSelectedDepartment(null);
    setCurrentView('process-generator');
  };

  const handleShowMermaidEditor = () => {
    setSelectedDepartment(null);
    setCurrentView('mermaid-editor');
  };

  const handleShowPapersRefiner = () => {
    setSelectedDepartment(null);
    setCurrentView('papers-refiner');
  };

  const handleShowStudio = () => {
    setSelectedDepartment(null);
    setCurrentView('studio');
  };

  const cycleTheme = () => {
    const themes: Array<'light' | 'dark' | 'nord'> = ['light', 'dark', 'nord'];
    const currentIndex = themes.indexOf(theme);
    const nextIndex = (currentIndex + 1) % themes.length;
    setTheme(themes[nextIndex]);
  };

  const handleAddDepartment = (newDept: {
    name: string;
    image: string;
    page: {
      title: string;
      benefit: string;
      features: string[];
    };
  }) => {
    const department: Department = {
      id: `dept-${Date.now()}`,
      name: newDept.name,
      image: newDept.image,
      page: newDept.page,
    };

    setDepartments([...departments, department]);
  };

  return (
    <div className="dashboard">
      <div className="light-effect light-effect-1"></div>
      <div className="light-effect light-effect-2"></div>
      <div className="light-effect light-effect-3"></div>
      <div className="geometric-lines"></div>

      <header className="dashboard-header">
        <div className="header-left">
          <h1>Mein Dashboard</h1>
          <nav className="header-nav">
            <button
              className={`nav-link ${currentView === 'papers-refiner' ? 'active' : ''}`}
              onClick={handleShowPapersRefiner}
            >
              Papers Refiner
            </button>
            <button
              className={`nav-link ${currentView === 'start' || currentView === 'detail' ? 'active' : ''}`}
              onClick={handleBack}
            >
              Departments
            </button>
            <button
              className={`nav-link ${currentView === 'process-generator' ? 'active' : ''}`}
              onClick={handleShowProcessGenerator}
            >
              Prozessgenerator
            </button>
            <button
              className={`nav-link ${currentView === 'mermaid-editor' ? 'active' : ''}`}
              onClick={handleShowMermaidEditor}
            >
              Mermaid Editor
            </button>
            <button
              className={`nav-link ${currentView === 'studio' ? 'active' : ''}`}
              onClick={handleShowStudio}
            >
              Claude Studio
            </button>
            <a
              className="nav-link"
              href="/prompting"
            >
              Prompts
            </a>
          </nav>
        </div>
        <ThemeSelector theme={theme} onCycle={cycleTheme} />
      </header>

      <div className="dashboard-content">
        {selectedDepartment && currentView === 'detail' && (
          <Sidebar
            department={selectedDepartment}
            onBack={handleBack}
          />
        )}

        <main className="dashboard-main">
          {currentView === 'studio' ? (
            <Studio />
          ) : currentView === 'papers-refiner' ? (
            <PapersRefiner />
          ) : currentView === 'mermaid-editor' ? (
            <MermaidEditorPage />
          ) : currentView === 'process-generator' ? (
            <ProcessGenerator />
          ) : selectedDepartment && currentView === 'detail' ? (
            <DetailPage department={selectedDepartment} />
          ) : (
            <StartView
              departments={departments}
              onSelect={handleDepartmentSelect}
              onAddNew={() => setShowAddForm(true)}
            />
          )}
        </main>
      </div>

      {showAddForm && (
        <AddDepartmentForm
          onAdd={handleAddDepartment}
          onClose={() => setShowAddForm(false)}
        />
      )}
    </div>
  );
};

export default Dashboard;
